import React from 'react';
import { Users, CheckCircle, Truck, Package, HeadphonesIcon, ArrowUpRight, Sparkles } from 'lucide-react';

const Services = () => {
  const services = [
    {
      icon: Users,
      title: 'Intermediação Internacional',
      description: 'Conectamos você aos melhores fornecedores globais com negociações vantajosas e parcerias sólidas.',
      badge: 'Popular',
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      icon: CheckCircle,
      title: 'Curadoria de Fornecedores',
      description: 'Seleção rigorosa com critérios de qualidade, confiabilidade e procedência internacional.',
      badge: 'Essencial',
      gradient: 'from-[#C9A961] to-[#b89850]'
    },
    {
      icon: Truck,
      title: 'Gestão Logística Completa',
      description: 'Gerenciamento de toda cadeia logística com acompanhamento em tempo real de cada etapa.',
      badge: 'Premium',
      gradient: 'from-purple-500 to-pink-500'
    },
    {
      icon: Package,
      title: 'Importação sob Demanda',
      description: 'Soluções personalizadas para importação de produtos específicos do seu negócio.',
      badge: 'Flexível',
      gradient: 'from-green-500 to-emerald-500'
    },
    {
      icon: HeadphonesIcon,
      title: 'Consultoria Especializada',
      description: 'Orientação expert para redução de custos e otimização de resultados em importação.',
      badge: 'Expert',
      gradient: 'from-orange-500 to-red-500'
    }
  ];

  return (
    <section id="services" className="py-32 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#2D5F7F] rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#C9A961] rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-2 bg-[#2D5F7F]/10 text-[#2D5F7F] text-sm font-semibold px-4 py-2 rounded-full mb-6">
            <Sparkles className="w-4 h-4" />
            Nossos Serviços
          </div>
          <h2 className="text-5xl md:text-6xl font-extrabold text-gray-900 mb-6">
            Soluções Completas em{' '}
            <span className="bg-gradient-to-r from-[#2D5F7F] to-[#C9A961] bg-clip-text text-transparent">
              Importação
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Do primeiro contato à entrega final, cuidamos de cada detalhe para o sucesso da sua operação
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-20 max-w-7xl mx-auto">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <div
                key={index}
                className="group relative bg-white rounded-3xl p-10 shadow-lg hover:shadow-2xl transition-all border-2 border-transparent hover:border-[#C9A961]/20 hover:-translate-y-1"
              >
                {/* Badge */}
                <div className="absolute top-6 right-6">
                  <span className={`bg-gradient-to-r ${service.gradient} text-white text-xs font-bold px-3 py-1 rounded-full`}>
                    {service.badge}
                  </span>
                </div>

                {/* Icon */}
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${service.gradient} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg`}>
                  <Icon className="w-8 h-8 text-white" />
                </div>

                {/* Content */}
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {service.title}
                </h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  {service.description}
                </p>

                {/* Arrow */}
                <div className="flex items-center text-[#2D5F7F] font-semibold group-hover:translate-x-2 transition-transform">
                  <span className="mr-2">Saiba mais</span>
                  <ArrowUpRight className="w-5 h-5" />
                </div>

                {/* Decorative Element */}
                <div className="absolute bottom-0 right-0 w-32 h-32 bg-gradient-to-tl from-gray-50 to-transparent rounded-3xl -z-10"></div>
              </div>
            );
          })}
        </div>

        {/* CTA Section */}
        <div className="max-w-5xl mx-auto">
          <div className="relative bg-gradient-to-br from-[#2D5F7F] via-[#1e4159] to-[#0f1419] rounded-3xl p-12 md:p-16 overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#C9A961] rounded-full blur-3xl"></div>
            </div>

            <div className="relative z-10 text-center">
              <h3 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Pronto para transformar sua operação?
              </h3>
              <p className="text-xl text-gray-200 mb-10 max-w-2xl mx-auto">
                Junte-se a centenas de empresas que já confiam na Inter Products para suas operações de importação
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => {
                    const element = document.getElementById('contact');
                    if (element) element.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="group bg-[#C9A961] hover:bg-[#b89850] text-gray-900 font-bold px-10 py-5 rounded-xl shadow-xl hover:shadow-2xl transition-all transform hover:scale-105"
                >
                  <span className="flex items-center justify-center gap-2">
                    Solicitar Orçamento
                    <ArrowUpRight className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                  </span>
                </button>
                <button
                  onClick={() => {
                    window.open('tel:+552199565-9798', '_self');
                  }}
                  className="bg-white/10 backdrop-blur-sm border-2 border-white/30 text-white hover:bg-white hover:text-gray-900 font-semibold px-10 py-5 rounded-xl transition-all"
                >
                  Falar com Especialista
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
